import styled from 'styled-components'

import { isDefined, Nullable } from 'shared/utils/type-guards'

import { RateTypeEnum } from '~/common/types/graphql'
import Text from '~/views/components/ui/Text'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

const HoursInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

const HourContainer = styled.span(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${theme.spacing(1)};
`,
)

const HourSummary = ({ label = '', value = 0 }) => (
  <HourContainer>
    <span>{label}</span>
    <span data-testid={`${label}-hours-value`}>{value}</span>
  </HourContainer>
)

type HourDetails = {
  estimated?: number
  actual?: number
  remaining?: number
  logged?: number
  nonBillableHours?: number
  billableHours?: number
  remainingPurchasedHours?: number
}

type HoursInfoDetailsProps = {
  hours?: HourDetails | null
  project?: {
    organization?: Nullable<{ timeTrackingEnabled?: Nullable<boolean> }>
    projectBillRate?: Nullable<{
      rateType?: Nullable<RateTypeEnum>
      hours?: Nullable<number>
    }>
  }
  isProvider?: boolean
}

export default function HoursInfoDetails(props: HoursInfoDetailsProps) {
  return (
    <HoursInfoContainer data-component-name={'hours-info-details'}>
      <HoursInfoContent {...props} />
    </HoursInfoContainer>
  )
}

function HoursInfoContent({
  project,
  hours,
  isProvider,
}: HoursInfoDetailsProps) {
  const [getCommonLabel] = useStaticLabels(['common'])
  if (!isDefined<HourDetails>(hours)) return null

  const {
    estimated: estimatedHours = 0,
    remaining: remainingHours = 0,
    actual: actualHours = 0,
    logged = 0,
    nonBillableHours = 0,
    billableHours = 0,
    remainingPurchasedHours = 0,
  } = hours

  const timeReportingV2IsEnabled = project?.organization?.timeTrackingEnabled
  if (!timeReportingV2IsEnabled) {
    return (
      <LegacyHours
        actualHours={actualHours}
        estimatedHours={estimatedHours}
        labels={{
          actualHours: getCommonLabel('actualHours'),
          estimatedHours: getCommonLabel('estimatedHours'),
        }}
      />
    )
  }

  if (isProvider) {
    return (
      <ProviderHours
        project={project}
        billableHours={billableHours}
        nonBillableHours={nonBillableHours}
        remainingPurchasedHours={remainingPurchasedHours}
        estimatedHours={estimatedHours}
        loggedHours={logged}
        labels={{
          billableHours: getCommonLabel('billableHours'),
          nonBillableHours: getCommonLabel('nonBillableHours'),
          remainingPurchasedHours: getCommonLabel('remainingPurchasedHours'),
          estimatedHours: getCommonLabel('estimatedHours'),
          loggedHours: getCommonLabel('loggedHours'),
        }}
      />
    )
  }

  return (
    <NonProviderHours
      remainingHours={remainingHours}
      estimatedHours={estimatedHours}
      labels={{
        openHours: getCommonLabel('openHours'),
        estimatedHours: getCommonLabel('estimatedHours'),
      }}
    />
  )
}

type NonProviderHoursProps = {
  remainingHours: number
  estimatedHours: number
  labels: {
    // "Open Hours"
    openHours: string

    // "Estimated Hours"
    estimatedHours: string
  }
}

function NonProviderHours(props: NonProviderHoursProps) {
  const { remainingHours, estimatedHours, labels } = props

  return (
    <>
      <Text size="medium" color="lightGray">
        <HourSummary label={labels.openHours} value={remainingHours} />
      </Text>

      <Text size="medium" color="lightGray">
        <HourSummary label={labels.estimatedHours} value={estimatedHours} />
      </Text>
    </>
  )
}

type ProviderHoursProps = {
  project?: {
    projectBillRate?: Nullable<{
      rateType?: Nullable<RateTypeEnum>
      hours?: Nullable<number>
    }>
  }
  billableHours: number
  nonBillableHours: number
  remainingPurchasedHours: number
  estimatedHours: number
  loggedHours: number
  labels: {
    // "Billable Hours"
    billableHours: string

    // "Non Billable Hours"
    nonBillableHours: string

    // "Remaining Purchased Hours"
    remainingPurchasedHours: string

    // "Estimated Hours"
    estimatedHours: string

    // "Logged Hours"
    loggedHours: string
  }
}

function ProviderHours(props: ProviderHoursProps) {
  const {
    project,
    billableHours,
    nonBillableHours,
    remainingPurchasedHours,
    estimatedHours,
    loggedHours,
    labels,
  } = props

  const rateType = project?.projectBillRate?.rateType
  const hours = project?.projectBillRate?.hours

  const isFixedRate = rateType === RateTypeEnum.FixedRate
  const isVariableRate = rateType === RateTypeEnum.VariableRate
  const isFixedDollars = rateType === RateTypeEnum.FixedDollars

  return (
    <>
      {(isFixedRate || isVariableRate) && (
        <Text size="medium" color="lightGray">
          <HourSummary label={labels.billableHours} value={billableHours} />
        </Text>
      )}

      {(isFixedRate || isVariableRate) && (
        <Text size="medium" color="lightGray">
          <HourSummary
            label={labels.nonBillableHours}
            value={nonBillableHours}
          />
        </Text>
      )}

      {isFixedDollars && hours !== null && (
        <Text size="medium" color="lightGray">
          <HourSummary
            label={labels.remainingPurchasedHours}
            value={remainingPurchasedHours}
          />
        </Text>
      )}

      <Text size="medium" color="lightGray">
        <HourSummary label={labels.estimatedHours} value={estimatedHours} />
      </Text>

      <Text size="medium" color="lightGray">
        <HourSummary label={labels.loggedHours} value={loggedHours} />
      </Text>
    </>
  )
}

type LegacyHoursProps = {
  actualHours: number
  estimatedHours: number
  labels: {
    // "Actual Hours"
    actualHours: string

    // "Estimated Hours"
    estimatedHours: string
  }
}

function LegacyHours(props: LegacyHoursProps) {
  const { actualHours, estimatedHours, labels } = props

  return (
    <>
      <Text size="medium" color="lightGray">
        <HourSummary label={labels.actualHours} value={actualHours} />
      </Text>

      <Text size="medium" color="lightGray">
        <HourSummary label={labels.estimatedHours} value={estimatedHours} />
      </Text>
    </>
  )
}
